/**
 * Weight Ticket Mutation Hooks
 * React Query mutations for creating, updating, approving, and rejecting tickets
 *
 * Following DDD Principles:
 * - Mutations call service layer methods
 * - Optimistic updates for better UX
 * - Automatic cache invalidation
 * - Toast notifications for feedback
 * - NO business logic in hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceProviderService } from '@/services/service-provider.service';
import type {
  CreateTicketDto,
  WeightTicket,
  TicketPhoto,
} from '@/services/service-provider.service';
import { toast } from 'sonner';
import { ticketKeys } from './useWeightTicketData';

/**
 * Hook to create a weight ticket
 */
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation<WeightTicket, Error, CreateTicketDto>({
    mutationFn: (ticket: CreateTicketDto) =>
      serviceProviderService.createTicket(ticket),
    onSuccess: (newTicket) => {
      // Invalidate ticket list to trigger refetch
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.pendingReview() });

      // Show success toast
      toast.success(`Weight ticket ${newTicket.ticket_number} created successfully`);
    },
    onError: (error) => {
      // Show error toast with backend message
      toast.error(error.message || 'Failed to create weight ticket');
    },
  });
}

/**
 * Hook to update a weight ticket
 */
export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation<
    WeightTicket,
    Error,
    { ticketId: string; updates: Partial<CreateTicketDto> },
    { previousTicket: unknown }
  >({
    mutationFn: ({ ticketId, updates }) =>
      serviceProviderService.updateTicket(ticketId, updates),
    onMutate: async ({ ticketId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ticketKeys.detail(ticketId) });

      // Snapshot the previous value for rollback
      const previousTicket = queryClient.getQueryData(ticketKeys.detail(ticketId));

      return { previousTicket };
    },
    onSuccess: (updatedTicket) => {
      // Update the ticket detail cache
      queryClient.setQueryData(ticketKeys.detail(updatedTicket.id), updatedTicket);

      // Invalidate ticket list to trigger refetch
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });

      // Show success toast
      toast.success(`Ticket ${updatedTicket.ticket_number} updated successfully`);
    },
    onError: (error, { ticketId }, context) => {
      // Rollback to previous value on error
      if (context?.previousTicket) {
        queryClient.setQueryData(ticketKeys.detail(ticketId), context.previousTicket);
      }

      // Show error toast with backend message
      toast.error(error.message || 'Failed to update ticket');
    },
  });
}

/**
 * Hook to approve a weight ticket
 */
export function useApproveTicket() {
  const queryClient = useQueryClient();

  return useMutation<WeightTicket, Error, string, { previousTicket: unknown }>({
    mutationFn: (ticketId: string) => serviceProviderService.approveTicket(ticketId),
    onMutate: async (ticketId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ticketKeys.detail(ticketId) });

      // Snapshot the previous value for rollback
      const previousTicket = queryClient.getQueryData(ticketKeys.detail(ticketId));

      return { previousTicket };
    },
    onSuccess: (approvedTicket) => {
      // Update the ticket detail cache
      queryClient.setQueryData(ticketKeys.detail(approvedTicket.id), approvedTicket);

      // Invalidate ticket list to trigger refetch
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.pendingReview() });

      // Show success toast
      toast.success(`Ticket ${approvedTicket.ticket_number} approved`);
    },
    onError: (error, ticketId, context) => {
      // Rollback to previous value on error
      if (context?.previousTicket) {
        queryClient.setQueryData(ticketKeys.detail(ticketId), context.previousTicket);
      }

      // Show error toast with backend message
      toast.error(error.message || 'Failed to approve ticket');
    },
  });
}

/**
 * Hook to reject a weight ticket
 */
export function useRejectTicket() {
  const queryClient = useQueryClient();

  return useMutation<
    WeightTicket,
    Error,
    { ticketId: string; reason: string },
    { previousTicket: unknown }
  >({
    mutationFn: ({ ticketId, reason }) =>
      serviceProviderService.rejectTicket(ticketId, reason),
    onMutate: async ({ ticketId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ticketKeys.detail(ticketId) });

      // Snapshot the previous value for rollback
      const previousTicket = queryClient.getQueryData(ticketKeys.detail(ticketId));

      return { previousTicket };
    },
    onSuccess: (rejectedTicket) => {
      // Update the ticket detail cache
      queryClient.setQueryData(ticketKeys.detail(rejectedTicket.id), rejectedTicket);

      // Invalidate ticket list to trigger refetch
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.pendingReview() });

      // Show success toast
      toast.success(`Ticket ${rejectedTicket.ticket_number} rejected`);
    },
    onError: (error, { ticketId }, context) => {
      // Rollback to previous value on error
      if (context?.previousTicket) {
        queryClient.setQueryData(ticketKeys.detail(ticketId), context.previousTicket);
      }

      // Show error toast with backend message
      toast.error(error.message || 'Failed to reject ticket');
    },
  });
}

/**
 * Hook to upload a photo to a weight ticket
 */
export function useUploadTicketPhoto() {
  const queryClient = useQueryClient();

  return useMutation<
    TicketPhoto,
    Error,
    { ticketId: string; photo: File },
    { uploadProgress?: number }
  >({
    mutationFn: ({ ticketId, photo }) =>
      serviceProviderService.uploadTicketPhoto(ticketId, photo),
    onSuccess: (_newPhoto, { ticketId }) => {
      // Invalidate ticket detail to trigger refetch (includes new photo)
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });

      // Show success toast
      toast.success('Photo uploaded successfully');
    },
    onError: (error) => {
      // Show error toast with backend message
      toast.error(error.message || 'Failed to upload photo');
    },
  });
}
